/**
 * Trustpilot consent-gated loader (PECR Reg. 6).
 *
 * The Trustpilot bootstrap script is loaded dynamically ONLY after the user
 * grants analytics cookies (analytics_storage: 'granted'). If consent is
 * denied (or not yet given), the widget container falls back to a static
 * "Trustpilot" link — no third-party cookies are set.
 *
 * Listens for the `consent_update` dataLayer event pushed by ConsentBanner.
 * British English project.
 */

const TRUSTPILOT_SCRIPT_SRC = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
let loaded = false;

/** Load the Trustpilot bootstrap script (once). */
function loadTrustpilotScript(): void {
  if (loaded || typeof document === 'undefined') return;
  loaded = true;
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = TRUSTPILOT_SCRIPT_SRC;
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Check the current consent state + load the script if analytics is granted.
 * Called on boot + whenever the consent state changes.
 */
function checkConsentAndLoad(): void {
  if (typeof window === 'undefined') return;
  const dataLayer = (window as unknown as { dataLayer?: Array<Record<string, unknown>> }).dataLayer ?? [];
  // Check the most recent consent_update event for analytics_storage.
  for (let i = dataLayer.length - 1; i >= 0; i--) {
    const entry = dataLayer[i];
    if (entry?.event === 'consent_update' && entry.analytics_storage === 'granted') {
      loadTrustpilotScript();
      return;
    }
  }
  // Also check localStorage (the consent choice persists there).
  try {
    if (localStorage.getItem('itk-consent') === 'granted') {
      loadTrustpilotScript();
    }
  } catch {
    // localStorage unavailable (SSR / privacy mode) — skip.
  }
}

/**
 * Initialise the consent-gated Trustpilot loader. Call once on app boot.
 * Checks the existing consent state + listens for future consent changes.
 */
export function initTrustpilotLoader(): void {
  if (typeof window === 'undefined') return;
  // Check the existing consent state on boot.
  checkConsentAndLoad();
  // Listen for future consent_update events on the dataLayer (push interception).
  const dataLayer = (window as unknown as { dataLayer?: unknown[] }).dataLayer ?? [];
  (window as unknown as { dataLayer: unknown[] }).dataLayer = dataLayer;
  const originalPush = dataLayer.push.bind(dataLayer);
  dataLayer.push = (...args: unknown[]) => {
    for (const arg of args) {
      if (
        typeof arg === 'object' &&
        arg !== null &&
        (arg as Record<string, unknown>).event === 'consent_update'
      ) {
        checkConsentAndLoad();
        break;
      }
    }
    return originalPush(...args);
  };
}